'use client';

/**
 * Shared pieces of a camp row: the photo, the fact lines and the detail link.
 * Used by the night card, its Swap sheet and the leg list so a camp reads the
 * same everywhere.
 */

import React, { useState } from 'react';
import { withBase } from '../../lib/brand';
import { toOptimizedImageSrc } from '../../utils/normalizeImageSrc';
import { campPowerLabel, campPriceSlot, campTypeLabel, type CampOption, type FeeTone } from '../../utils/campsAlong';
import { AERIAL_ATTRIBUTION, aerialLayers } from '../../utils/aerialThumb';
import { campsiteSlug } from '../TripNightsPlanner';

export const campHref = (c: Pick<CampOption, 'name'>) => withBase(`/camp-sites/${campsiteSlug(c.name)}`);

/* ── No photo: a tinted tile that says what kind of place it is ─────────── */
// Four looks, chosen from the listing type and the name: a caravan park
// (lake blue), a bush camp (eucalypt green), a rest area (sage, a picnic
// table) and a pub or roadhouse (ochre).
// A soft gradient, a low ridge line and a confident icon read as a designed
// tile, not a broken image.

type Kind = 'park' | 'camp' | 'rest' | 'stop';

const svgUri = (svg: string) => 'data:image/svg+xml;utf8,' + encodeURIComponent(svg);

const TILE: Record<Kind, { from: string; to: string; ridge: string; ink: string; icon: string }> = {
  park: {
    from: '#e3eefb',
    to: '#c9dcf3',
    ridge: '#b4cdec',
    ink: '#24508f',
    icon: '<path d="M19 38V27.5a3 3 0 0 1 3-3h14a6 6 0 0 1 6 6V38H19Z"/><path d="M24 29.5h6M36 29.5v4.5M42 38h4"/><circle cx="27" cy="39" r="2.6" fill="#24508f" stroke="none"/>',
  },
  camp: {
    from: '#e4f1ea',
    to: '#c6e0d2',
    ridge: '#aed2bf',
    ink: '#1f6b4f',
    icon: '<path d="M32 20 20 40h24L32 20Z"/><path d="M32 29.5 28 40M32 29.5 36 40"/><path d="M17 40h30"/>',
  },
  rest: {
    from: '#eef1e4',
    to: '#d9e0c4',
    ridge: '#c8d2ab',
    ink: '#4d5e22',
    icon: '<path d="M20 28h24M23 28l-3 12M41 28l3 12M18 34h28M26 34l-2 6M38 34l2 6"/><path d="M27 21h10"/>',
  },
  stop: {
    from: '#f6eddc',
    to: '#ecd9b6',
    ridge: '#e2c99c',
    ink: '#8a5a12',
    icon: '<path d="M22 40V24h20v16"/><path d="M19 24l13-7 13 7"/><path d="M29 40v-8h6v8"/>',
  },
};

const NO_PHOTO: Record<Kind, string> = Object.fromEntries(
  (Object.keys(TILE) as Kind[]).map((k) => {
    const t = TILE[k];
    return [
      k,
      svgUri(
        `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="${t.from}"/><stop offset="1" stop-color="${t.to}"/></linearGradient></defs><rect width="64" height="64" fill="url(#g)"/><path d="M0 52c10-6 18-8 27-4s19 3 37-6v22H0Z" fill="${t.ridge}" opacity=".7"/><g fill="none" stroke="${t.ink}" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">${t.icon}</g></svg>`
      ),
    ];
  })
) as Record<Kind, string>;

const REST_NAME = /\b(rest area|rest stop|truck stop|parking bay|picnic area|roadside stop)\b/i;
const VENUE_NAME = /\b(hotel|pub|tavern|inn|motel|roadhouse)\b/i;

export function campKind(type: string | null | undefined, name?: string | null): Kind {
  if (name && REST_NAME.test(name)) return 'rest';
  if (name && VENUE_NAME.test(name) && !/caravan|holiday park|tourist park/i.test(name)) return 'stop';
  return type === 'caravan_park' ? 'park' : 'camp';
}

export const noPhotoFor = (type: string | null | undefined, name?: string | null) => NO_PHOTO[campKind(type, name)];

/* A small glyph for the kind of place, on an aerial crop's corner. */
const KIND_GLYPH: Record<Kind, React.ReactNode> = {
  park: <path d="M5 15V10.5A1.5 1.5 0 0 1 6.5 9H14a3 3 0 0 1 3 3v3H5Zm4.5-3.5h2.5M17 15h2" />,
  camp: <path d="M12 6 6 17h12L12 6Zm0 5-2 6m2-6 2 6" />,
  rest: <path d="M6 11h12M7.5 11 6 17m10.5-6 1.5 6M5 14h14" />,
  stop: <path d="M7 17V10h10v7M5.5 10 12 6l6.5 4M10.5 17v-3.5h3V17" />,
};

/**
 * A camp's photo; with none, an aerial crop of the spot itself (every row
 * looks different and it IS the place), with a corner glyph saying what kind
 * of place it is. The tinted kind tile sits underneath both, so a slow or
 * failed load is a calm tile, never a blank grey square.
 * `n` overlays the pin number the map shares (top-left, like a map card).
 */
export function CampThumb({
  camp,
  size = 56,
  className = '',
  eager = false,
  src: srcOverride,
  n,
  lit = false,
  tone = 'blue',
}: {
  camp: Pick<CampOption, 'image' | 'name'> & { type?: string | null; lat?: number; lng?: number };
  size?: number;
  className?: string;
  eager?: boolean;
  /** A photo chosen elsewhere (the night card's outdoor pick). */
  src?: string | null;
  /** Pin number shared with the map. */
  n?: number;
  lit?: boolean;
  tone?: 'blue' | 'green';
}) {
  const [failed, setFailed] = useState<string | null>(null);
  const kind = campKind(camp.type, camp.name);
  const fallback = NO_PHOTO[kind];
  const raw = srcOverride !== undefined ? srcOverride : camp.image;
  const photo = raw && failed !== raw ? toOptimizedImageSrc(raw, { width: size * 2, quality: 70 }) : null;
  const aerial =
    !photo && typeof camp.lat === 'number' && typeof camp.lng === 'number' ? aerialLayers(camp.lat, camp.lng, size) : null;

  let body: React.ReactNode;
  if (photo) {
    body = (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={photo}
        alt=""
        width={size}
        height={size}
        loading={eager ? 'eager' : 'lazy'}
        draggable={false}
        decoding="async"
        onError={() => setFailed(raw ?? null)}
        style={{ width: size, height: size, backgroundImage: `url("${fallback}")`, backgroundSize: 'cover' }}
        className={`block flex-none rounded-tp object-cover ${className}`}
        data-photo="real"
      />
    );
  } else if (aerial) {
    // Real <img> tiles (2–4) positioned so the camp sits dead centre.
    body = (
      <span
        role="img"
        aria-label={`Aerial view of ${camp.name}`}
        title={AERIAL_ATTRIBUTION}
        className={`relative block flex-none overflow-hidden rounded-tp ${className}`}
        style={{ width: size, height: size, backgroundImage: `url("${fallback}")`, backgroundSize: 'cover' }}
        data-photo="aerial"
      >
        {aerial.map((t) => (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            key={t.url}
            src={t.url}
            alt=""
            width={t.size}
            height={t.size}
            loading={eager ? 'eager' : 'lazy'}
            decoding="async"
            draggable={false}
            className="absolute max-w-none"
            style={{ left: t.left, top: t.top, width: t.size, height: t.size }}
            onError={(e) => {
              e.currentTarget.style.visibility = 'hidden';
            }}
          />
        ))}
        {/* The camp sits dead centre: a small ring marks the spot. */}
        <span
          className="absolute left-1/2 top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-[0_0_0_1px_rgba(0,0,0,0.3)]"
          aria-hidden="true"
        />
        <span
          className="absolute bottom-1 right-1 flex h-[18px] w-[18px] items-center justify-center rounded-full bg-white/95 shadow-[0_1px_2px_rgba(16,24,40,0.3)]"
          aria-hidden="true"
        >
          <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke={TILE[kind].ink} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            {KIND_GLYPH[kind]}
          </svg>
        </span>
      </span>
    );
  } else {
    body = (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={fallback}
        alt=""
        width={size}
        height={size}
        draggable={false}
        style={{ width: size, height: size }}
        className={`block flex-none rounded-tp object-cover ${className}`}
        data-photo="none"
      />
    );
  }
  if (typeof n !== 'number') return <>{body}</>;
  return (
    <span className="relative block flex-none" style={{ width: size, height: size }}>
      {body}
      <span className="absolute left-1 top-1">
        <PinNumber n={n} lit={lit} tone={tone} />
      </span>
    </span>
  );
}

/**
 * The pin number a row shares with the map: 22px, white with a blue ring;
 * filled when the row is the selected one (here or on the map).
 */
export function PinNumber({ n, lit = false, tone = 'blue' }: { n: number; lit?: boolean; tone?: 'blue' | 'green' }) {
  const fill = tone === 'green' ? 'bg-tp-ok text-white ring-white' : 'bg-tp-brand text-white ring-white';
  return (
    <span
      className={`flex h-[22px] min-w-[22px] flex-none items-center justify-center rounded-full px-1 text-[11.5px] font-bold tabular-nums shadow-[0_1px_2px_rgba(16,24,40,0.25)] ring-2 transition-colors duration-150 ${
        lit || tone === 'green' ? fill : 'bg-white text-tp-brand ring-white'
      }`}
      aria-hidden="true"
    >
      {n}
    </span>
  );
}

const TONE: Record<FeeTone, string> = {
  free: 'font-semibold text-tp-ok',
  paid: 'font-semibold text-tp-ink',
  note: 'font-semibold text-amber-800',
  closed: 'font-semibold text-red-700',
  unknown: 'font-medium text-tp-muted',
};

const Dot = () => (
  <span className="px-1 text-tp-faint" aria-hidden="true">
    ·
  </span>
);

/**
 * The same slots, in the same order, on every row:
 *   "From $38 · ★ 4.5 · Caravan park"      ("Price on listing · Rest area")
 *   "27 km from Lakes Entrance · 10 km detour"
 *   "Powered · Showers"
 * The price slot always leads and always means price: a rate, a verified
 * Free, or "Price on listing". Where-it-is wraps rather than ellipsizing;
 * 13px, gray-700/600 on white (≥ 7:1).
 */
export function CampFacts({
  camp,
  distance,
  lead,
}: {
  camp: CampOption;
  distance?: string | null;
  /** Leading figure on the second line ("27 km from Lakes Entrance"). */
  lead?: string | null;
}) {
  const price = campPriceSlot(camp);
  const power = campPowerLabel(camp);
  const where = [lead, distance].filter(Boolean) as string[];
  const amenity = camp.amenities?.[0] ?? null;
  return (
    <>
      <p className="mt-0.5 truncate text-tp-sm leading-snug text-tp-muted">
        <span className={`tabular-nums ${TONE[price.tone]}`} data-fee-label={price.tone}>
          {price.text}
        </span>
        {typeof camp.rating === 'number' && (
          <>
            <Dot />
            <span className="tabular-nums">
              <span className="text-amber-600" aria-hidden="true">★</span> {camp.rating.toFixed(1)}
              <span className="sr-only"> stars</span>
            </span>
          </>
        )}
        <Dot />
        <span>{campTypeLabel(camp)}</span>
      </p>
      {where.length > 0 && (
        <p className="mt-0.5 text-tp-sm leading-snug text-tp-muted">
          {where.map((t, i) => (
            <React.Fragment key={t}>
              {i > 0 && (
                <>
                  <Dot />
                  <wbr />
                </>
              )}
              {/* Each phrase stays whole; a long one wraps at the dot. */}
              <span className={`whitespace-nowrap tabular-nums ${i === 0 && lead ? 'font-medium text-tp-ink' : ''}`}>{t}</span>
            </React.Fragment>
          ))}
        </p>
      )}
      {(power || amenity) && (
        <p className="mt-0.5 truncate text-[12.5px] leading-snug text-tp-muted">
          {power && (
            <span>
              <BoltIcon className={`-mt-0.5 mr-0.5 inline h-3 w-3 ${camp.powered ? 'text-amber-600' : 'text-tp-faint'}`} />
              {power}
            </span>
          )}
          {power && amenity && <Dot />}
          {amenity && <span>{camp.amenities!.slice(0, 2).join(' · ')}</span>}
        </p>
      )}
    </>
  );
}

/** A lightning bolt: powered sites. */
function BoltIcon({ className = 'h-3.5 w-3.5' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <path d="M8.8 1.8 3.8 9h4l-.6 5.2L12.2 7h-4l.6-5.2Z" />
    </svg>
  );
}
